// file: `react/src/components/ChatscopeInbox/ChatscopeInbox.tsx`:

import React, { useMemo, useState } from 'react';
import {
  Sidebar,
  ConversationList,
  Conversation,
  ConversationHeader,
  Search,
  AddUserButton,
  StarButton,
  ExpansionPanel
} from '@chatscope/chat-ui-kit-react';
import { memberAvatarNode } from '../MemberAvatars.tsx';
import { isCiphertextPayload } from '../../crypto/messagePayload.ts';
import {
  threadArchived,
  threadMuted,
  threadPinned,
  threadUnreadCount
} from '../threadPrefs.ts';
import { roomSecurityLabel } from '../threadRoomType.ts';
import '../ChatscopeThread/ChatscopeThread.scss';
import './ChatscopeInbox.scss';

function formatThreadTime(iso?: string) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

export function previewText(last) {
  if (!last) return 'No messages yet';
  if (last.type === 'image') return last.text ? last.text : 'Photo';
  const text = (last.text || '').replace(/\s+/g, ' ').trim();
  if (!text) return 'Message';
  if (text.startsWith('{') && isCiphertextPayload(text)) return 'Encrypted message';
  return text;
}

function typingLine(names: string[] | undefined, myName?: string) {
  const others = (names || []).filter((name) => name && name !== myName);
  if (others.length === 0) return '';
  if (others.length === 1) return `${others[0]} is typing…`;
  if (others.length === 2) return `${others[0]} and ${others[1]} are typing…`;
  return 'Several people are typing…';
}

function ChatscopeInbox({
  threads,
  activeThreadId,
  myName,
  online = [],
  typing = {},
  onOpen,
  onNewConversation,
  headerExtra,
  onTogglePin
}) {
  const [query, setQuery] = useState('');

  const { active, archived } = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matches = (thread) => {
      if (!q) return true;
      const name = (thread.name || '').toLowerCase();
      const preview = previewText(thread.lastMessage).toLowerCase();
      const people = (thread.members || []).map((m) => `${m.shortName} ${m.fullName}`).join(' ').toLowerCase();
      return name.includes(q) || preview.includes(q) || people.includes(q);
    };
    const list = (threads || []).filter(matches);
    const pinned = [];
    const rest = [];
    const hidden = [];
    for (const thread of list) {
      if (threadArchived(thread) && !q) hidden.push(thread);
      else if (threadPinned(thread)) pinned.push(thread);
      else rest.push(thread);
    }
    return { active: [...pinned, ...rest], archived: hidden };
  }, [threads, query]);

  const renderConversation = (thread) => {
    const unread = threadUnreadCount(thread);
    const live = typingLine(typing[String(thread.id)], myName);
    const boundary = roomSecurityLabel(thread);
    const muted = threadMuted(thread) ? '🔇 ' : '';
    return (
      <Conversation
        key={thread.id}
        name={`${muted}${thread.name}`}
        lastSenderName={live ? undefined : thread.lastMessage?.sender}
        info={`${boundary} · ${live || previewText(thread.lastMessage)}`}
        lastActivityTime={formatThreadTime(thread.lastMessage?.time || thread.createdAt)}
        unreadDot={unread > 0 && unread < 2}
        unreadCnt={unread > 1 ? unread : undefined}
        active={String(thread.id) === String(activeThreadId)}
        onClick={() => onOpen && onOpen(thread.id)}
      >
        {memberAvatarNode({
          members: thread.members,
          online,
          fallback: (thread.name || '?').trim().charAt(0)
        })}
        <Conversation.Operations
          visible={threadPinned(thread)}
          onClick={(e) => e.stopPropagation()}
        >
          <StarButton
            title={threadPinned(thread) ? 'Unpin' : 'Pin'}
            className={threadPinned(thread) ? 'pinned' : ''}
            onClick={(e) => {
              e.stopPropagation();
              if (onTogglePin) onTogglePin(thread.id);
            }}
          />
        </Conversation.Operations>
      </Conversation>
    );
  };

  return (
    <Sidebar position="left" scrollable className="chatscope-inbox-sidebar">
      <ConversationHeader>
        <ConversationHeader.Content userName="Messages" />
        <ConversationHeader.Actions>
          {headerExtra}
          {onNewConversation && (
            <AddUserButton title="New conversation" onClick={onNewConversation} />
          )}
        </ConversationHeader.Actions>
      </ConversationHeader>
      <Search
        placeholder="Search conversations"
        value={query}
        onChange={(value) => setQuery(value)}
        onClearClick={() => setQuery('')}
      />
      <ConversationList>
        {active.map(renderConversation)}
      </ConversationList>
      {archived.length > 0 && (
        <ExpansionPanel title={`Archived (${archived.length})`}>
          <ConversationList>
            {archived.map(renderConversation)}
          </ConversationList>
        </ExpansionPanel>
      )}
    </Sidebar>
  );
}

export default ChatscopeInbox;
